/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import { generatePath, Link } from "react-router-dom";
import { Cell } from "react-table";
import styled from "styled-components";
import {
  Button,
  ExtendedTooltip,
  FlexContainer,
  SVGIcon,
  Tag,
  theme,
  Tooltip,
} from "@nordcloud/gnui";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools";
import { showRole } from "~/views/settings/UsersAndPermissions/showRole";
import { Account } from "../types";
import { showColor } from "../utils";

type BaseApplicationData = {
  nid: string;
  name: string;
};

const StyledTag = styled(Tag)`
  line-height: 1.2;
  padding: 0.4rem;
`;

export const UsersAndPermissionsColumns = (
  onRemove: (email: string) => void,
  onEdit?: (item: Account) => void
) => [
  {
    Header: "",
    accessor: "isOrgAdminFromPanel",
    width: "2%",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => (
      <ExtendedTooltip
        placement="right"
        caption={
          row.original.isOrgAdminFromPanel
            ? "Organization Admin created via MSP"
            : undefined
        }
      >
        <SVGIcon
          color={row.original.isOrgAdminFromPanel ? "success" : undefined}
          name={row.original.isOrgAdminFromPanel ? "userAccess" : "user"}
        />
      </ExtendedTooltip>
    ),
  },
  {
    Header: "Email",
    accessor: "email",
    width: "32%",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => <div>{row?.original?.email}</div>,
  },
  {
    Header: "Role",
    accessor: "access.role",
    disableSortBy: true,
    width: "18%",
    Cell: ({ row }: Cell<Account>) => (
      <FlexContainer>
        <StyledTag
          text={showRole(row?.original?.access?.role)}
          color={showColor(row?.original.access.role)}
        />
      </FlexContainer>
    ),
  },
  {
    Header: "Permissions",
    accessor: "access.applications",
    width: "35%",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => {
      return (
        <FlexContainer wrap="wrap">
          {row?.original?.access?.applicationsWithNames?.map(
            ({ nid, name }: BaseApplicationData) => (
              <Link
                key={nid}
                css={{ "&:hover": { textDecoration: "none" } }}
                to={generatePath(ROUTES.applications.details, {
                  application: nid,
                })}
              >
                <FlexContainer>
                  <Tag color="notification" text={name} />
                </FlexContainer>
              </Link>
            )
          )}
        </FlexContainer>
      );
    },
  },
  {
    Header: () => (
      <div css={{ position: "relative", textTransform: "none" }}>
        Last Login Date
        <Tooltip
          caption={`"-" means that the user logged in before July 28, 2023. The exact login date is unavailable.`}
        >
          <SVGIcon
            name="info"
            size="sm"
            css={{ marginLeft: theme.spacing.spacing01 }}
          />
        </Tooltip>
      </div>
    ),
    accessor: "lastLoginDate",
    width: "18%",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => {
      const userLoginDate = row?.original?.lastLoginDate;
      const displayLastLoginDate = isNotNil(userLoginDate)
        ? dayjs(userLoginDate).format(dateFormat.dayShortMonthYear)
        : "-";

      return <div>{displayLastLoginDate}</div>;
    },
  },
  {
    Header: "",
    accessor: "actions",
    disableSortBy: true,
    width: "6%",
    Cell: ({ row }: Cell<Account>) => (
      <FlexContainer
        columnGap={theme.spacing.spacing02}
        justifyContent="center"
      >
        <If condition={row.original.isOrgAdminFromPanel}>
          <Then>
            <ExtendedTooltip
              caption="Cannot remove & edit admins created via MSP"
              placement="left"
            >
              <SVGIcon name="info" />
            </ExtendedTooltip>
          </Then>
          <Else>
            <When condition={isNotNil(onEdit)}>
              <Button
                size="md"
                severity="low"
                icon="edit"
                onClick={() => {
                  onEdit?.(row?.original);
                }}
              />
            </When>
            <Button
              size="md"
              severity="low"
              icon="trash"
              onClick={() => onRemove(row?.original?.email)}
            />
          </Else>
        </If>
      </FlexContainer>
    ),
  },
];
